import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { ShieldCheck, Mail, Loader2 } from "lucide-react";
import { api, errorMessage } from "@/lib/api";
import { Button } from "@/components/ui/button";

const BASE = "/admin/reports/delivery/otp";
const IDLE_MS = 5 * 60 * 1000;
const TOUCH_MS = 60 * 1000;

export const OtpGate = ({ children }) => {
  const [status, setStatus] = useState(null);
  const [code, setCode] = useState("");
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);
  const [busy, setBusy] = useState(false);

  const refresh = () => api.get(`${BASE}/status`).then((r) => setStatus(r.data)).catch((e) => { setStatus({ error: e?.response?.status || 500 }); });
  useEffect(() => { refresh(); }, []);
  useEffect(() => {
    if (!status?.verified_until) return;
    const ms = new Date(status.verified_until) - Date.now();
    const t = setTimeout(refresh, Math.max(1000, ms + 500));
    return () => clearTimeout(t);
  }, [status]);

  // relock after 5 idle minutes; while the tab is active, heartbeat every minute so the server window slides with you
  const lastActivity = useRef(Date.now());
  const lastTouch = useRef(Date.now());
  const verified = !!status?.verified;
  useEffect(() => {
    if (!verified) return;
    const mark = () => { lastActivity.current = Date.now(); };
    const events = ["mousemove", "mousedown", "keydown", "scroll", "touchstart", "focus"];
    events.forEach((e) => window.addEventListener(e, mark, { passive: true }));
    lastActivity.current = Date.now();
    lastTouch.current = Date.now();
    const tick = setInterval(async () => {
      const idle = Date.now() - lastActivity.current;
      if (idle >= IDLE_MS) {
        try { await api.post(`${BASE}/lock`); } catch {}
        setStatus((s) => ({ ...s, verified: false, verified_until: null }));
        toast.message("Reporting locked after 5 minutes of inactivity");
        return;
      }
      if (Date.now() - lastTouch.current >= TOUCH_MS && lastActivity.current > lastTouch.current) {
        lastTouch.current = Date.now();
        try { const { data } = await api.post(`${BASE}/touch`); setStatus((s) => ({ ...s, verified_until: data.verified_until })); } catch { refresh(); }
      }
    }, 5000);
    return () => { clearInterval(tick); events.forEach((e) => window.removeEventListener(e, mark)); };
  }, [verified]);

  const send = async () => {
    setSending(true);
    try { const { data } = await api.post(`${BASE}/send`); setSent(true); toast.success("Code sent to the designated email address"); }
    catch (e) { toast.error(errorMessage(e)); }
    finally { setSending(false); }
  };
  const verify = async (e) => {
    e.preventDefault();
    setBusy(true);
    try { await api.post(`${BASE}/verify`, { code }); setCode(""); await refresh(); toast.success("Verified — Reporting unlocked for 15 minutes"); }
    catch (err) { toast.error(errorMessage(err)); }
    finally { setBusy(false); }
  };

  if (!status) return <p className="text-slate-400">Checking access…</p>;
  if (status.error) return <div className="card-surface rounded-2xl p-8 text-slate-400" data-testid="reports-not-found">This page isn't available.</div>;
  if (status.verified) return children({ verifiedUntil: status.verified_until, lock: async () => { await api.post(`${BASE}/lock`); refresh(); } });

  return (
    <div className="card-surface rounded-3xl p-8 max-w-lg" data-testid="reports-otp-gate">
      <div className="flex items-center gap-3"><span className="grid h-11 w-11 place-items-center rounded-2xl bg-amber-400/15 text-amber-300"><ShieldCheck size={22} /></span><div><p className="eyebrow">Extra security</p><h2 className="font-display text-xl font-bold">Confirm it's you</h2></div></div>
      <p className="mt-4 text-sm text-slate-400">Reporting is locked behind a one-time code. We'll email a 12-digit code to the designated email address; it works once and expires in 10 minutes. Reporting relocks after 5 minutes of inactivity.</p>
      <div className="mt-5 flex flex-wrap items-center justify-center gap-3">
        <Button type="button" onClick={send} disabled={sending} className="rounded-full bg-sky-400 text-[#070F1E] font-bold hover:bg-sky-300" data-testid="reports-otp-send">{sending ? <Loader2 size={15} className="mr-2 animate-spin" /> : <Mail size={15} className="mr-2" />} {sent ? "Send a new code" : "Email me a code"}</Button>
      </div>
      <form onSubmit={verify} className="mt-5 flex flex-col items-center gap-[26px]" data-testid="reports-otp-form">
        <label className="block text-center"><span className="block text-[10px] font-mono uppercase tracking-wider text-slate-500">12-digit code</span>
          <input value={code} onChange={(e) => setCode(e.target.value.replace(/\D/g, "").slice(0, 12))} inputMode="numeric" autoComplete="one-time-code" placeholder="•••• •••• ••••" className="mt-1 h-11 w-72 rounded-xl bg-white/5 border border-white/10 px-4 font-mono text-lg tracking-[0.25em] text-center outline-none focus:border-sky-400" data-testid="reports-otp-input" />
        </label>
        <Button type="submit" disabled={busy || code.length !== 12} className="h-11 rounded-full bg-emerald-500 text-white font-bold hover:bg-emerald-400" data-testid="reports-otp-verify">{busy ? "Checking…" : "Unlock Reporting"}</Button>
      </form>
    </div>
  );
};
